import { useEffect, useMemo, useState } from 'react'
import { Section, TableScroller, useBearer, usePagedQuery, useToast } from './_admin-utils'

interface Shipment {
  id?: string
  tracking?: string
  carrier?: string
  status?: string
}

interface OrderItem {
  id: string
  name?: string
  amount_cents?: number
  category?: string
  qty?: number
}

interface Order {
  id: string
  teacher_id?: string
  budget_id?: string
  status?: string
  placed_at?: string
  total_cents?: number
  order_items?: OrderItem[]
  shipments?: Shipment[]
}

const STATUS_FILTERS = ['all', 'submitted', 'paid', 'fulfilled', 'canceled']

function formatCurrency(cents: number | null | undefined): string {
  if (!Number.isFinite(cents)) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((cents || 0) / 100)
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString()
}

export default function AdminOrders() {
  const bearer = useBearer()
  const { show, ToastHost } = useToast()
  const [status, setStatus] = useState<string>('submitted')
  const [teacherId, setTeacherId] = useState<string>('')
  const [selected, setSelected] = useState<Order | null>(null)

  const fetcher = useMemo(() => {
    return async ({ page, pageSize }: { page: number; pageSize: number }): Promise<{ items: Order[]; total: number }> => {
      if (!bearer) return { items: [], total: 0 }
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) })
      if (status && status !== 'all') params.set('status', status)
      if (teacherId.trim()) params.set('teacher_id', teacherId.trim())
      const CORE_API = import.meta.env.VITE_CORE_API_URL || ''
      const response = await fetch(`${CORE_API}/api/v1/orders?${params.toString()}`, {
        headers: { ...bearer },
      })
      const raw = await response.json().catch(() => ({})) as { data?: { items?: Order[]; total?: number }; items?: Order[]; total?: number; error?: { message?: string } | string }
      if (!response.ok) {
        const errMsg = typeof raw?.error === 'string' ? raw.error : (raw?.error as any)?.message
        throw new Error(errMsg || 'Failed to load orders')
      }
      const payload = (raw?.data ?? raw) as { items?: Order[]; total?: number }
      return { items: payload.items || [], total: payload.total || 0 }
    }
  }, [bearer, status, teacherId])

  const { items, page, pageCount, load, loading, error } = usePagedQuery<Order>(fetcher, [fetcher], 20)

  const transition = async (order: Order, nextStatus: string) => {
    if (!bearer) return
    const CORE_API = import.meta.env.VITE_CORE_API_URL || ''
    const response = await fetch(`${CORE_API}/api/v1/orders/status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...bearer },
      body: JSON.stringify({ order_id: order.id, status: nextStatus }),
    })
    const statusRaw = await response.json().catch(() => ({})) as { error?: { message?: string } | string }
    if (!response.ok) {
      const errMsg = typeof statusRaw?.error === 'string' ? statusRaw.error : (statusRaw?.error as any)?.message
      alert(errMsg || 'Unable to update order')
      return
    }
    show(`Order marked ${nextStatus}`)
    load(page)
  }

  const updateShipment = async (order: Order) => {
    if (!bearer) return
    const tracking = window.prompt('Tracking number', order.shipments?.[0]?.tracking || '')
    if (tracking === null) return
    const carrier = window.prompt('Carrier', order.shipments?.[0]?.carrier || '')
    if (carrier === null) return
    const statusPrompt = window.prompt('Shipment status', order.shipments?.[0]?.status || 'in_transit')
    if (statusPrompt === null) return
    const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
    const response = await fetch(`${CORE_API}/api/v1/admin/shipments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...bearer },
      body: JSON.stringify({
        order_id: order.id,
        shipment_id: order.shipments?.[0]?.id,
        carrier: carrier || null,
        tracking: tracking || null,
        status: statusPrompt || null,
      }),
    })
    const payload = await response.json().catch(() => ({})) as { error?: string }
    if (!response.ok) {
      alert(payload?.error || 'Unable to update shipment')
      return
    }
    show('Shipment updated')
    load(page)
  }

  useEffect(() => {
    if (!selected) return
    const latest = items.find((order) => order.id === selected.id)
    if (latest && latest !== selected) setSelected(latest)
  }, [items, selected])

  return (
    <Section
      title="Orders"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-cocoa/60">
            {STATUS_FILTERS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  setStatus(option)
                  load(1)
                }}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                  status === option ? 'bg-cocoa text-white' : 'bg-shell/60 text-cocoa/70 hover:bg-shell'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={teacherId}
            onChange={(event) => setTeacherId(event.target.value)}
            placeholder="Teacher ID"
            className="rounded-lg border border-white/70 bg-shell/40 px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={() => load(1)}
            className="rounded-full border border-white/70 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-cocoa hover:bg-shell"
          >
            Apply
          </button>
        </div>
      }
    >
      <ToastHost />
      <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
        <div>
          {loading ? (
            <p className="text-sm text-cocoa/70">Loading orders…</p>
          ) : error ? (
            <p className="text-sm text-brick">{error}</p>
          ) : items.length ? (
            <TableScroller>
              <table className="min-w-full divide-y divide-white/60 text-sm text-cocoa/80">
                <thead className="bg-shell/60 text-xs uppercase tracking-wide text-cocoa/60">
                  <tr>
                    <th className="px-3 py-2 text-left">Order</th>
                    <th className="px-3 py-2 text-left">Teacher</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Placed</th>
                    <th className="px-3 py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/60">
                  {items.map((order) => (
                    <tr
                      key={order.id}
                      className={`cursor-pointer transition ${selected?.id === order.id ? 'bg-shell/60' : 'hover:bg-shell/40'}`}
                      onClick={() => setSelected(order)}
                    >
                      <td className="px-3 py-3 font-mono text-xs">{order.id}</td>
                      <td className="px-3 py-3 text-xs text-cocoa/60">{order.teacher_id}</td>
                      <td className="px-3 py-3 capitalize">{order.status}</td>
                      <td className="px-3 py-3 text-xs text-cocoa/60">{formatDate(order.placed_at)}</td>
                      <td className="px-3 py-3 text-right font-semibold text-cocoa">{formatCurrency(order.total_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroller>
          ) : (
            <p className="text-sm text-cocoa/70">No orders found.</p>
          )}
          <div className="mt-4 flex justify-end gap-3 text-xs uppercase tracking-wide text-cocoa/60">
            <button
              type="button"
              onClick={() => load(Math.max(page - 1, 1))}
              disabled={page <= 1}
              className="rounded-full border border-white/70 px-3 py-1.5 text-cocoa disabled:opacity-50"
            >
              Previous
            </button>
            <span>
              Page {page} of {pageCount}
            </span>
            <button
              type="button"
              onClick={() => load(Math.min(page + 1, pageCount))}
              disabled={page >= pageCount}
              className="rounded-full border border-white/70 px-3 py-1.5 text-cocoa disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {selected ? (
            <div className="rounded-2xl border border-white/70 bg-white p-4 shadow-sm">
              <header className="space-y-1">
                <h2 className="text-lg font-semibold text-cocoa">Order {selected.id}</h2>
                <p className="text-sm text-cocoa/60">Budget {selected.budget_id || '—'}</p>
              </header>
              <div className="mt-3 space-y-2 text-sm text-cocoa/70">
                {(selected.order_items || []).map((item) => (
                  <div key={item.id} className="rounded-xl border border-white/70 bg-shell/40 p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-cocoa">{item.name}</span>
                      <span className="font-semibold text-cocoa">{formatCurrency(item.amount_cents)}</span>
                    </div>
                    <p className="text-xs text-cocoa/60">{item.category || 'Uncategorized'} · Qty {item.qty}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => transition(selected, 'paid')}
                  className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white"
                >
                  Mark paid
                </button>
                <button
                  type="button"
                  onClick={() => transition(selected, 'fulfilled')}
                  className="rounded-full bg-sky-600 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white"
                >
                  Mark fulfilled
                </button>
                <button
                  type="button"
                  onClick={() => transition(selected, 'canceled')}
                  className="rounded-full bg-brick px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white"
                >
                  Cancel order
                </button>
                <button
                  type="button"
                  onClick={() => updateShipment(selected)}
                  className="rounded-full border border-white/70 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-cocoa hover:bg-shell"
                >
                  Update shipment
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-white/70 p-6 text-sm text-cocoa/60">Select an order to review.</div>
          )}
        </div>
      </div>
    </Section>
  )
}
